import { safeSetDoc, safeDeleteDoc, getActiveLocationsForDate } from '../utils';
import React, { useMemo, useState } from 'react';
import { usePersistentState } from '../hooks/usePersistentState';
import { refreshRecommendations } from '../hooks/useRecommendations';
import { useTags } from '../hooks/useTags';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc } from 'firebase/firestore';
import { appId } from '../constants';
import MapSearchBox from './MapSearchBox';
import { ChevronDown, ChevronUp, ExternalLink, Trash2, Settings, X } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { checkPlacesRateLimit } from '../utils';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const extractCity = (address) => {
  if (!address) return 'Unknown';
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 3) {
    const hasUSA = parts[parts.length - 1] === 'USA';
    const offset = hasUSA ? 2 : 1;
    if (parts.length > offset) return parts[parts.length - 1 - offset];
  }
  return 'Unknown';
};

const parseTimeStr = (timeStr) => {
  // Handles typical Google Places time formats like "9:00 AM", "10:30 PM"
  const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (match) {
    let h = parseInt(match[1]);
    let m = parseInt(match[2]);
    const ampm = match[3].toUpperCase();
    if (ampm === 'PM' && h < 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }
  return null;
};

const isOpenNow = (weekdayDescriptions) => {
  if (!weekdayDescriptions || weekdayDescriptions.length === 0) return null;
  
  const now = new Date();
  const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTotalMinutes = currentHour * 60 + currentMinute;

  const todayDesc = weekdayDescriptions.find(d => d.startsWith(currentDay));
  if (!todayDesc) return null;
  
  const parts = todayDesc.split(': ');
  if (parts.length < 2) return null;
  const hoursStr = parts.slice(1).join(': ');
  
  if (!hoursStr || hoursStr === 'Closed') return false;
  if (hoursStr === 'Open 24 hours') return true;

  const periods = hoursStr.split(', ');
  for (const period of periods) {
    const times = period.split(/\s*[-–]\s*/);
    if (times.length === 2) {
      const startMins = parseTimeStr(times[0]);
      const endMins = parseTimeStr(times[1]);
      
      if (startMins !== null && endMins !== null) {
        if (endMins < startMins) {
          if (currentTotalMinutes >= startMins || currentTotalMinutes <= endMins) return true;
        } else {
          if (currentTotalMinutes >= startMins && currentTotalMinutes <= endMins) return true;
        }
      }
    }
  }
  return false;
};

const getTodayHours = (weekdayDescriptions) => {
  if (!weekdayDescriptions || weekdayDescriptions.length === 0) return 'Unknown';
  const currentDay = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const todayDesc = weekdayDescriptions.find(d => d.startsWith(currentDay));
  if (!todayDesc) return 'Unknown';
  return todayDesc.split(': ').slice(1).join(': ');
};





const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export default function DatabaseView({ 
  isOpen = false, 
  calendarItems = EMPTY_ARRAY, 
  activeFilters = EMPTY_ARRAY, 
  visibleCalendars = EMPTY_ARRAY,
  profile,
  lat: defaultLat, 
  lng: defaultLng, 
  locationName: defaultName,
  mapStartDate,
  mapEndDate,
  selectedTripId,
  trips = [],
  friendFilter = 'all',
  allUsers = [],
  allTasks = [],
  habits = [],
  authUser,
  searchRadius,
  customCenter,
  dispatch,
  recommendations = EMPTY_ARRAY,
  aliases = EMPTY_ARRAY,
  tagsHierarchy = EMPTY_OBJECT,
  availableTags = EMPTY_ARRAY,
  getParentTags,
  getChildTags
}) {
  const { saveTagsHierarchy, loading: tagsLoading } = useTags();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = usePersistentState('nytm.database.filters.activeTab', 'places');
  const [isDatabaseOverride, setIsDatabaseOverride] = usePersistentState('nytm.database.filters.isDatabaseOverride', false); // 'places' | 'events'
  const [selectedCities, setSelectedCities] = usePersistentState('nytm.database.filters.selectedCities', []);
  const [selectedTags, setSelectedTags] = usePersistentState('nytm.database.filters.selectedTags', []);
  const [openStatus, setOpenStatus] = usePersistentState('nytm.database.filters.openStatus', 'Any'); // 'Any', 'Open Now', 'Sunday', etc.
  const [sourceFilter, setSourceFilter] = usePersistentState('nytm.database.filters.sourceFilter', 'All'); // 'All', 'Me', 'Friends'
  const [searchQuery, setSearchQuery] = usePersistentState('nytm.database.filters.searchQuery', '');
  const [tagSearchQuery, setTagSearchQuery] = usePersistentState('nytm.database.filters.tagSearchQuery', '');
  const [isTagsExpanded, setIsTagsExpanded] = usePersistentState('nytm.database.filters.isTagsExpanded', false);
  const [sortConfig, setSortConfig] = usePersistentState('nytm.database.filters.sortConfig', { key: 'createdAt', direction: 'desc' });
  
  // Power User State
  const [selectedPlaceIds, setSelectedPlaceIds] = useState(new Set());
  const [externalPlaceId, setExternalPlaceId] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [resultLimit, setResultLimit] = useState(100);

  const parentRef = React.useRef(null);
  const isAdmin = auth.currentUser?.email === 'brett.gaddy@gmail.com';

  // Aggregate recommendations by placeId
  
  const activeRecommendations = useMemo(() => {
    if (isDatabaseOverride) return recommendations;
    
    const showFood = visibleCalendars?.includes('nytm-restaurant');
    const showDrinks = visibleCalendars?.includes('nytm-drinks');
    const showCoffee = visibleCalendars?.includes('nytm-coffee');
    const showSights = visibleCalendars?.includes('nytm-sight');
    const showShopping = visibleCalendars?.includes('nytm-shopping');
    const showWantToGo = visibleCalendars?.includes('nytm-wanttogo');
    const showMusicVenues = visibleCalendars?.includes('nytm-musicvenue');

    if (!showFood && !showDrinks && !showCoffee && !showSights && !showShopping && !showWantToGo && !showMusicVenues) {
      return [];
    }
    
    return recommendations.filter(rec => {
      

      // 1. Check if user matches friendFilter
      let userMatches = false;
      if (friendFilter === 'all') {
        const friendEmails = (profile?.friends || []).map(e => e.toLowerCase());
        const isMyRec = rec.userId === authUser?.uid;
        
        let isFriendRec = false;
        if (!isMyRec) {
           const recUser = allUsers.find(u => u.id === rec.userId);
           if (recUser && recUser.email && friendEmails.includes(recUser.email.toLowerCase())) {
             isFriendRec = true;
           }
        }
        userMatches = isMyRec || isFriendRec;
      } else if (friendFilter === 'me') {
        userMatches = rec.userId === authUser?.uid;
      } else {
        userMatches = rec.userId === friendFilter;
      }

      if (!userMatches) return false;

      // Filter by active tags (advancedFilters passed as activeFilters)
      const selectedTags = activeFilters?.filter(f => f.startsWith('tag-')).map(f => f.replace('tag-', '')) || [];
      if (selectedTags.length > 0) {
        const hasMatchingTag = selectedTags.some(t => rec.tags?.includes(t));
        if (!hasMatchingTag) return false;
      }

      // Filter by visible icon categories
      const isFood = rec.tags?.some(t => getParentTags(t).includes('Food') || t === 'Food');
      const isDrinks = rec.tags?.some(t => getParentTags(t).includes('Drink') || t === 'Drink');
      const isCoffee = rec.tags?.some(t => getParentTags(t).includes('Coffee') || t === 'Coffee');
      const isSight = rec.tags?.some(t => getParentTags(t).includes('Sights') || t === 'Sights' || getParentTags(t).includes('Sight') || t === 'Sight');
      const isShopping = rec.tags?.some(t => getParentTags(t).includes('Shopping') || t === 'Shopping');
      const isMusicVenue = rec.tags?.some(t => getParentTags(t).includes('Music Venue') || t === 'Music Venue');
      const isWantToGo = rec.tags?.some(t => typeof t === 'string' && t.toLowerCase() === 'want to go');
      
      if (!showFood && !showDrinks && !showCoffee && !showSights && !showShopping && !showMusicVenues) return false;

      const activeTypes = [];
      let matchesPrimary = false;
      if (showFood && isFood) { activeTypes.push('restaurant'); matchesPrimary = true; }
      if (showDrinks && isDrinks) { activeTypes.push('drinks'); matchesPrimary = true; }
      if (showCoffee && isCoffee) { activeTypes.push('coffee'); matchesPrimary = true; }
      if (showSights && isSight) { activeTypes.push('sight'); matchesPrimary = true; }
      if (showShopping && isShopping) { activeTypes.push('shopping'); matchesPrimary = true; }
      if (showMusicVenues && isMusicVenue) { activeTypes.push('musicvenue'); matchesPrimary = true; }
      
      if (!matchesPrimary) return false;

      if (showWantToGo !== isWantToGo) return false;
      
      // Store the activeTypes array on the record itself for the next map phase
      rec._activeTypes = activeTypes;

      // 3. Determine active locations and dates to check against
      let datesToCheck = [];
      const getLocationsForDate = (dateStr, dateObj) => {
        return getActiveLocationsForDate(dateStr, dateObj, trips, calendarItems, profile?.cities?.[0]);
      };

      if (customCenter) {
        datesToCheck = [{ lat: customCenter.lat, lng: customCenter.lng, date: new Date() }];
      } else if (mapStartDate && mapEndDate) {
        let curr = new Date(mapStartDate + 'T12:00:00');
        const end = new Date(mapEndDate + 'T12:00:00');
        while (curr <= end) {
          const dStr = curr.toISOString().split('T')[0];
          datesToCheck = datesToCheck.concat(getLocationsForDate(dStr, new Date(curr)));
          curr.setDate(curr.getDate() + 1);
        }
      } else {
        const todayObj = new Date();
        const todayStr = new Date(todayObj.getTime() - (todayObj.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
        datesToCheck = getLocationsForDate(todayStr, todayObj);
      }

      // Check if rec matches any active location AND is open on that day
      let matchFound = false;
      let minDistance = Infinity;
      
      if (isWantToGo) {
        matchFound = true; // Bypass location check for bucket list items
      }
      
      for (const loc of datesToCheck) {
        // Fast bounding box check (1 degree lat ~= 69 miles)
        const latDelta = searchRadius / 69;
        const lonDelta = searchRadius / Math.max(10, 69 * Math.cos(loc.lat * Math.PI / 180));
        if (Math.abs(rec.lat - loc.lat) > latDelta || Math.abs(rec.lng - loc.lng) > lonDelta) {
          continue;
        }

        const R = 3958.8; // miles
        const dLat = (rec.lat - loc.lat) * Math.PI / 180;
        const dLon = (rec.lng - loc.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(loc.lat * Math.PI / 180) * Math.cos(rec.lat * Math.PI / 180) *
                  Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c;

        if (distance <= searchRadius) {
           // Check if open
           let isOpen = true;
           if (rec.weekdayDescriptions && rec.weekdayDescriptions.length === 7) {
               const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
               const dayName = dayNames[loc.date.getDay()];
               const desc = rec.weekdayDescriptions.find(d => d.startsWith(dayName));
               if (desc && desc.toLowerCase().includes('closed')) {
                 const nowMs = new Date().getTime();
                 const updatedAt = rec.updatedAt?.seconds * 1000 || rec.createdAt?.seconds * 1000 || 0;
                 if (nowMs - updatedAt <= 7 * 24 * 60 * 60 * 1000) {
                   isOpen = false;
                 }
               }
           }
           if (isOpen) {
             matchFound = true;
             if (distance < minDistance) minDistance = distance;
           }
        }
      }
      if (matchFound) rec.minDistance = minDistance;
      return matchFound;
    });
  }, [recommendations, activeFilters, visibleCalendars, mapStartDate, mapEndDate, profile, friendFilter, authUser, allUsers, searchRadius, customCenter, trips, calendarItems, isDatabaseOverride, getParentTags]);

  const places = useMemo(() => {
    const recsByPlace = {};
    activeRecommendations.forEach(rec => {
      const isMe = rec.userId === auth.currentUser?.uid;
      const userName = isMe ? 'Me' : (allUsers?.find(u => u.uid === rec.userId || u.id === rec.userId)?.displayName || 'Unknown');

      if (!recsByPlace[rec.placeId]) {
        recsByPlace[rec.placeId] = {
          placeId: rec.placeId,
          name: rec.placeName || 'Unknown',
          address: rec.address || '',
          city: extractCity(rec.address),
          lat: rec.lat,
          lng: rec.lng,
          tags: new Set(rec.tags || []),
          users: new Set([rec.userId]),
          
          // Store individual user data
          userContributions: {
            [rec.userId]: {
              name: userName,
              isMe: isMe,
              note: rec.note || '',
              happyHourNote: rec.happyHourNote || '',
              tags: rec.tags || []
            }
          },

          weekdayDescriptions: rec.weekdayDescriptions || [],
          createdAt: rec.createdAt?.seconds || 0,
          myDocId: null,
          myNote: '',
          myTags: []
        };
      } else {
        recsByPlace[rec.placeId].users.add(rec.userId);
        
        // Add to contributions
        recsByPlace[rec.placeId].userContributions[rec.userId] = {
          name: userName,
          isMe: isMe,
          note: rec.note || '',
          happyHourNote: rec.happyHourNote || '',
          tags: rec.tags || []
        };

        rec.tags?.forEach(t => recsByPlace[rec.placeId].tags.add(t));
        
        // Keep earliest creation date
        if (rec.createdAt?.seconds && rec.createdAt.seconds < recsByPlace[rec.placeId].createdAt) {
          recsByPlace[rec.placeId].createdAt = rec.createdAt.seconds;
        }
      }

      if (isMe) {
        recsByPlace[rec.placeId].myDocId = `${rec.userId}_${rec.placeId}`;
        recsByPlace[rec.placeId].myNote = rec.note || '';
        recsByPlace[rec.placeId].myTags = rec.tags || [];
      }
    });

    return Object.values(recsByPlace).map(r => {
      const isMe = r.users.has(auth.currentUser?.uid);
      const otherUsers = Array.from(r.users)
        .filter(uid => uid !== auth.currentUser?.uid)
        .map(uid => allUsers?.find(u => u.uid === uid || u.id === uid)?.displayName || 'Unknown');
      
      let addedByStr = '';
      if (isMe && otherUsers.length > 0) {
        addedByStr = `Me, ${otherUsers.join(', ')}`;
      } else if (isMe) {
        addedByStr = 'Me';
      } else if (otherUsers.length > 0) {
        addedByStr = otherUsers.join(', ');
      } else {
        addedByStr = 'Unknown';
      }

      // Prepare notes array for the search filter and generic display
      const allNotes = Object.values(r.userContributions)
        .filter(c => c.note)
        .map(c => `${c.name}: ${c.note}`);

      // Find Alias
      const existingAlias = aliases.find(a => a.placeId === r.placeId && a.userId === auth.currentUser?.uid);
      
      return {
        ...r,
        alias: existingAlias ? existingAlias.alias : '',
        tags: Array.from(r.tags).sort(),
        usersArray: Array.from(r.users),
        isAddedByMe: isMe,
        isAddedByFriends: otherUsers.length > 0,
        addedByString: addedByStr,
        notes: allNotes,
        contributionsList: Object.values(r.userContributions),
        isOpenNow: isOpenNow(r.weekdayDescriptions),
        todayHours: getTodayHours(r.weekdayDescriptions)
      };
    });
  }, [activeRecommendations, allUsers]);

  // Extract unique filters
  const uniqueCities = useMemo(() => Array.from(new Set(places.map(p => p.city))).sort(), [places]);
  const uniqueTags = useMemo(() => Array.from(new Set(places.flatMap(p => p.tags))).sort(), [places]);

  // Filtering
  const filteredPlaces = useMemo(() => {
    return places.filter(p => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const nameMatch = p.name?.toLowerCase().includes(q);
        const aliasMatch = p.alias?.toLowerCase().includes(q);
        if (!nameMatch && !aliasMatch) return false;
      }
      if (selectedCities.length > 0 && !selectedCities.includes(p.city)) {
        return false;
      }
      if (selectedTags.length > 0) {
        const hasAllTags = selectedTags.every(t => p.tags.includes(t));
        if (!hasAllTags) return false;
      }
      return true;
    });
  }, [places, searchQuery, selectedCities, selectedTags]);

  // Sorting
  const sortedPlaces = useMemo(() => {
    return [...filteredPlaces].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredPlaces, sortConfig, allUsers]);

  // --- EVENTS LOGIC ---
  const [eventSearchQuery, setEventSearchQuery] = usePersistentState('nytm.database.filters.eventSearchQuery', '');
  const [eventSortConfig, setEventSortConfig] = usePersistentState('nytm.database.filters.eventSortConfig', { key: 'startDate', direction: 'asc' });

  const processedEvents = useMemo(() => {
    let eventsList = calendarItems.filter(i => i.type === 'event' || i.type === 'tentative');

    // Date Filter (Applies to mapStartDate/mapEndDate if set)
    if (mapStartDate || mapEndDate) {
      eventsList = eventsList.filter(e => {
        if (mapStartDate && e.endDate < mapStartDate) return false;
        if (mapEndDate && e.startDate > mapEndDate) return false;
        return true;
      });
    }

    // Search filter
    if (eventSearchQuery) {
      const q = eventSearchQuery.toLowerCase();
      eventsList = eventsList.filter(e => 
        (e.title || '').toLowerCase().includes(q) ||
        (e.city || '').toLowerCase().includes(q) ||
        (e.address || '').toLowerCase().includes(q) ||
        (e.venueName || '').toLowerCase().includes(q) ||
        (e.note || '').toLowerCase().includes(q)
      );
    }

    // Sorting
    return eventsList.sort((a, b) => {
      let aVal = a[eventSortConfig.key] || '';
      let bVal = b[eventSortConfig.key] || '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return eventSortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return eventSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [calendarItems, mapStartDate, mapEndDate, eventSearchQuery, eventSortConfig]);

  const handleEventSort = (key) => {
    let direction = 'asc';
    if (eventSortConfig.key === key && eventSortConfig.direction === 'asc') direction = 'desc';
    setEventSortConfig({ key, direction });
  };

  const renderEventSortIcon = (key) => {
    if (eventSortConfig.key !== key) return <span className="w-4" />;
    return <span className="w-4 text-center">{eventSortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
  };
  // --------------------

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const rowVirtualizer = useVirtualizer({
    count: Math.min(sortedPlaces.length, resultLimit),
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    overscan: 5,
  });

  const renderSortIcon = (column) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp size={14} className="inline ml-1" /> : <ChevronDown size={14} className="inline ml-1" />;
  };

  const toggleRowSelection = (placeId) => {
    const next = new Set(selectedPlaceIds);
    if (next.has(placeId)) next.delete(placeId);
    else next.add(placeId);
    setSelectedPlaceIds(next);
  };

  const toggleAllVisibleSelection = () => {
    if (selectedPlaceIds.size === sortedPlaces.filter(p => p.isAddedByMe).length) {
      setSelectedPlaceIds(new Set());
    } else {
      setSelectedPlaceIds(new Set(sortedPlaces.filter(p => p.isAddedByMe).map(p => p.placeId)));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedPlaceIds.size} places?`)) return;
    
    let didDelete = false;
    for (const pid of selectedPlaceIds) {
      const place = places.find(p => p.placeId === pid);
      if (place && place.myDocId) {
        await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'recommendations', place.myDocId)).catch(console.error);
        didDelete = true;
      }
    }
    if (didDelete) {
      await refreshRecommendations();
    }
    setSelectedPlaceIds(new Set());
  };

  const handleBulkSync = async () => {
    if (!window.confirm(`Syncing data for ${selectedPlaceIds.size} places may take a moment. Proceed?`)) return;
    setIsSyncing(true);
    
    try {
      const placesLib = await window.google.maps.importLibrary("places");
      const { serverTimestamp, setDoc } = await import('firebase/firestore');

      for (const pid of selectedPlaceIds) {
        const place = places.find(p => p.placeId === pid);
        if (!place) continue;
        
        try {
          if (!checkPlacesRateLimit()) {
            console.warn('[DatabaseView] Rate limit hit. Aborting sync.');
            break;
          }
          const p = new placesLib.Place({ id: place.placeId });
          await p.fetchFields({ fields: ['regularOpeningHours', 'editorialSummary', 'websiteURI'] });
          
          const updates = { updatedAt: serverTimestamp(), summaryUpdatedAt: serverTimestamp() };
          const placesUpdates = { updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.uid || 'unknown' };
          if (p.regularOpeningHours) {
            updates.weekdayDescriptions = p.regularOpeningHours.weekdayDescriptions || [];
            placesUpdates.hours = p.regularOpeningHours.weekdayDescriptions || [];
          }
          if (p.editorialSummary) updates.summary = p.editorialSummary;
          if (p.websiteURI) {
            updates.website = p.websiteURI;
            placesUpdates.website = p.websiteURI;
          }
          
          for (const uId of place.usersArray) {
            await safeSetDoc(doc(db, 'artifacts', appId, 'public', 'data', 'recommendations', `${uId}_${place.placeId}`), updates, { merge: true });
          }
          await safeSetDoc(doc(db, 'artifacts', appId, 'public', 'data', 'places', place.placeId), placesUpdates, { merge: true });
        } catch (e) {
          console.error(`Failed syncing place ${pid}:`, e);
        }
      }
      await refreshRecommendations();
    } catch (err) {
      console.error("Bulk sync error:", err);
    }
    
    setIsSyncing(false);
    setSelectedPlaceIds(new Set());
  };



  if (!isOpen) return null;

  if (tagsLoading) {
    return (
      <div className="bg-white border-t border-zinc-200 relative flex flex-col items-center justify-center overflow-hidden transition-all duration-300 ease-in-out" style={{ height: '75%' }}>
        <div className="p-8 text-zinc-500 font-medium">Loading database...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="bg-zinc-50 border-t border-zinc-200 relative flex flex-col items-center justify-center overflow-hidden transition-all duration-300 ease-in-out" style={{ height: '75%' }}>
        <h1 className="text-2xl font-black text-zinc-900 tracking-tight mb-2">Access Denied</h1>
        <p className="text-zinc-500 font-medium text-center">You do not have permission to view the database. This is an admin feature.</p>
        <button onClick={() => window.location.href = '/calendar'} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700">Go Back</button>
      </div>
    );
  }

  return (
    <div className="bg-white border-t border-zinc-200 relative flex flex-col overflow-hidden transition-all duration-300 ease-in-out" style={{ height: '75%' }}>
      <div className="flex-1 flex overflow-hidden">

      {/* Bulk Action Bar */}
      {selectedPlaceIds.size > 0 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-zinc-900 text-white rounded-full shadow-2xl px-4 py-2 flex items-center gap-4 z-50 animate-in slide-in-from-bottom-5 duration-200 border border-zinc-700">
          <span className="text-xs font-black uppercase tracking-widest pl-2">
            {selectedPlaceIds.size} Selected
          </span>
          <div className="w-px h-4 bg-zinc-700 mx-1" />
          <button 
            onClick={handleBulkSync}
            disabled={isSyncing}
            className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors ${isSyncing ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed' : 'hover:bg-zinc-800'}`}
          >
            {isSyncing ? 'Syncing...' : 'Sync Google Data'}
          </button>
          <div className="w-px h-4 bg-zinc-700 mx-1" />
          <button 
            onClick={handleBulkDelete}
            className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full hover:bg-red-900/50 hover:text-red-400 text-red-500 transition-colors"
          >
            Delete
          </button>
          <div className="w-px h-4 bg-zinc-700 mx-1" />
          <button 
            onClick={() => setSelectedPlaceIds(new Set())}
            className="p-1 hover:bg-zinc-800 rounded-full transition-colors text-zinc-400 hover:text-white"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Table Area */}
      <div className="flex-1 p-2 relative flex flex-col gap-2">
        
        {/* Tabs */}
        
        <div className="flex items-center justify-between border-b border-zinc-200 px-2 pb-1">
          <div className="flex gap-1">
            <button 
              onClick={() => setActiveTab('places')}
              className={`px-4 py-2 font-black uppercase tracking-widest text-[10px] transition-colors ${activeTab === 'places' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              Recommendations
            </button>
            <button 
              onClick={() => setActiveTab('events')}
              className={`px-4 py-2 font-black uppercase tracking-widest text-[10px] transition-colors ${activeTab === 'events' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              Events & Plans
            </button>
            <button 
              onClick={() => setActiveTab('tasks')}
              className={`px-4 py-2 font-black uppercase tracking-widest text-[10px] transition-colors ${activeTab === 'tasks' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              Tasks
            </button>
            <button 
              onClick={() => setActiveTab('habits')}
              className={`px-4 py-2 font-black uppercase tracking-widest text-[10px] transition-colors ${activeTab === 'habits' ? 'text-zinc-900 border-b-2 border-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            >
              Habits
            </button>
          </div>
          {activeTab === 'places' && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-zinc-600 hover:text-zinc-900 transition-colors">
                <input 
                  type="checkbox" 
                  checked={isDatabaseOverride}
                  onChange={(e) => setIsDatabaseOverride(e.target.checked)}
                  className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                />
                OVERRIDE FILTERS
              </label>
              <div className="w-px h-4 bg-zinc-200" />
              <button 
                onClick={() => navigate('/database/tags')}
                className="flex items-center justify-center gap-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 rounded-lg transition-colors border border-zinc-200"
              >
                <Settings size={12} strokeWidth={2.5} />
                Manage Tags
              </button>
              <div className="relative w-[250px] z-50">
                 <MapSearchBox overlayMode={true} dropdownAlign="right" recommendations={recommendations} externalPlaceId={externalPlaceId} onClearSearch={() => setExternalPlaceId(null)} className="relative w-full z-[100]" />
              </div>
            </div>
          )}
        </div>


        <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm flex-1 flex flex-col overflow-hidden">
          {activeTab === 'places' ? (
            <>
          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3 px-3 py-2 bg-white border-b border-zinc-200 shrink-0 z-10">
            <input 
              type="text"
              placeholder="Filter by name or alias..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 px-3 text-xs font-medium border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-48 text-zinc-900 placeholder-zinc-400 bg-white"
            />
            
            <select
              value={selectedCities.length > 0 ? selectedCities[0] : ''}
              onChange={(e) => setSelectedCities(e.target.value ? [e.target.value] : [])}
              className="h-8 px-3 text-xs font-medium border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-zinc-900 cursor-pointer min-w-[140px]"
            >
              <option value="">All Locations</option>
              {uniqueCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>

            <select
              value={selectedTags.length > 0 ? selectedTags[0] : ''}
              onChange={(e) => setSelectedTags(e.target.value ? [e.target.value] : [])}
              className="h-8 px-3 text-xs font-medium border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-zinc-900 cursor-pointer min-w-[140px]"
            >
              <option value="">All Tags</option>
              {uniqueTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>

            <div className="h-4 w-px bg-zinc-200 mx-1 hidden sm:block"></div>
            
            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
              {filteredPlaces.length} {filteredPlaces.length === 1 ? 'Result' : 'Results'}
            </span>

            {(searchQuery || selectedCities.length > 0 || selectedTags.length > 0) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCities([]);
                  setSelectedTags([]);
                }}
                className="ml-auto flex items-center justify-center gap-1.5 h-8 px-3 text-[10px] font-black uppercase tracking-widest bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 rounded-lg transition-colors border border-zinc-200"
              >
                <X size={12} strokeWidth={2.5} />
                Clear
              </button>
            )}
          </div>

          {/* Header Row (Fixed) */}
          <div className="flex bg-zinc-50 border-b border-zinc-200 select-none shrink-0 pr-4"> {/* pr-4 for scrollbar compensation */}
            <div className="px-3 py-1.5 w-[5%] flex items-center justify-center">
              <input 
                type="checkbox" 
                className="w-4 h-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                checked={sortedPlaces.filter(p => p.isAddedByMe).length > 0 && selectedPlaceIds.size === sortedPlaces.filter(p => p.isAddedByMe).length}
                onChange={toggleAllVisibleSelection}
              />
            </div>
            <div onClick={() => handleSort('name')} className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] cursor-pointer hover:text-zinc-800 transition-colors w-[20%] flex items-center">
              Place Name {renderSortIcon('name')}
            </div>
            <div onClick={() => handleSort('alias')} className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] cursor-pointer hover:text-zinc-800 transition-colors w-[15%] flex items-center">
              Alias {renderSortIcon('alias')}
            </div>
            <div onClick={() => handleSort('city')} className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] cursor-pointer hover:text-zinc-800 transition-colors w-[10%] flex items-center">
              Location {renderSortIcon('city')}
            </div>
            <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[15%] flex items-center">
              Tags
            </div>
            <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[10%] flex items-center">
              Today's Hours
            </div>
            <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[15%] flex items-center">
              Notes
            </div>
            <div onClick={() => handleSort('createdAt')} className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] cursor-pointer hover:text-zinc-800 transition-colors w-[10%] flex items-center justify-end">
              Added {renderSortIcon('createdAt')}
            </div>
          </div>

          {/* Virtualized Body */}
          <div ref={parentRef} className="flex-1 overflow-auto custom-scrollbar relative pb-24">
            {sortedPlaces.length === 0 ? (
              <div className="py-16 text-center text-zinc-400 font-bold text-sm">No places match your filters.</div>
            ) : (
              <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
                {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const place = sortedPlaces[virtualRow.index];
                  const isSelected = selectedPlaceIds.has(place.placeId);
                  return (
                    <div
                      key={virtualRow.key}
                      ref={rowVirtualizer.measureElement}
                      data-index={virtualRow.index}
                      onClick={() => setExternalPlaceId(place.placeId)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                      className={`flex min-h-[40px] items-stretch border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${isSelected ? 'bg-blue-50 hover:bg-blue-50' : ''}`}
                    >
                        <div className="w-[5%] flex items-center justify-center border-r border-zinc-50 px-2 py-1.5 shrink-0">
                          {place.isAddedByMe ? (
                            <input 
                              type="checkbox" 
                              className="w-4 h-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleRowSelection(place.placeId);
                              }}
                            />
                          ) : (
                            <div className="w-4 h-4 rounded border border-zinc-200 bg-zinc-50 opacity-50" title="Cannot edit places you did not add" />
                          )}
                        </div>

                        <div className={`px-3 py-1.5 align-top w-[20%] flex flex-col justify-start min-w-0`}>
                          <div className="flex items-center gap-2 mb-0.5 relative group min-w-0">
                            {place.isAddedByMe && (
                              <div className="absolute -left-5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center pr-1">
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Are you sure you want to remove "${place.name}" from your database?`)) {
                                      await safeDeleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'recommendations', `${auth.currentUser.uid}_${place.placeId}`))
                                        .catch(err => console.error("Error removing place:", err));
                                      await refreshRecommendations();
                                    }
                                  }}
                                  className="text-red-400 hover:text-red-600 transition-colors p-0.5"
                                  title="Remove Place"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            )}
                            <a 
                              href={`https://www.google.com/maps/place/?q=place_id:${place.placeId}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className={`font-bold text-zinc-900 hover:text-blue-600 transition-colors flex items-center gap-1.5 max-w-full min-w-0 text-xs`}
                            >
                              <span className="truncate min-w-0">{place.name}</span>
                              <ExternalLink size={10} className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                            </a>
                          </div>
                        </div>

                        <div className={`px-3 py-1.5 align-top text-zinc-600 font-medium text-[10px] w-[15%] truncate`}>
                          {place.alias}
                        </div>

                        <div className={`px-3 py-1.5 align-top text-zinc-600 font-semibold text-[10px] w-[10%] truncate`}>
                          {place.city}
                        </div>

                        <div className={`px-3 py-1.5 align-top w-[15%] flex flex-col justify-start relative`}>
                          <div className="flex flex-wrap gap-1 overflow-hidden max-h-[30px] items-center">
                            {place.tags.map((t, i) => {
                              return (
                                <span key={i} className="px-1 py-0.5 font-black uppercase tracking-wider text-[8px] rounded border truncate max-w-full transition-colors flex items-center gap-0.5 bg-zinc-100 text-zinc-600 border-zinc-200">
                                  {t}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        <div className={`px-3 py-1.5 align-top w-[15%] flex flex-col justify-start gap-1 min-w-0`}>
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className={`w-1.5 h-1.5 shrink-0 rounded-full ${place.isOpenNow === true ? 'bg-green-500' : place.isOpenNow === false ? 'bg-red-500' : 'bg-zinc-300'}`} />
                            <span className="font-bold text-[10px] text-zinc-700 truncate">{place.todayHours}</span>
                          </div>
                        </div>

                        <div className={`px-3 py-1.5 align-top w-[15%] flex flex-col justify-start min-w-0`}>
                            <div className={`w-full min-h-[20px] min-w-0`}>
                              {place.contributionsList.some(c => c.note) ? (
                                <p className="text-[10px] text-zinc-600 font-medium italic truncate">
                                  "{place.contributionsList.find(c => c.note)?.note}"
                                </p>
                              ) : <span className="text-xs text-zinc-300 font-medium">-</span>}
                            </div>
                        </div>

                        <div className={`px-3 py-1.5 align-top w-[10%] flex flex-col justify-start items-end text-right`}>
                          <span className="text-[9px] font-bold text-zinc-600">
                            {place.createdAt ? new Date(place.createdAt * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : 'Unknown'}
                          </span>
                        </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
            </>
          ) : activeTab === 'events' ? (
            <>
              {/* Events Header Row */}
              <div className="flex bg-zinc-50 border-b border-zinc-200 select-none shrink-0">
                <div onClick={() => handleEventSort('title')} className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] cursor-pointer hover:text-zinc-800 transition-colors w-[25%] flex items-center">
                  Event Name {renderEventSortIcon('title')}
                </div>
                <div onClick={() => handleEventSort('startDate')} className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] cursor-pointer hover:text-zinc-800 transition-colors w-[15%] flex items-center">
                  Start Date {renderEventSortIcon('startDate')}
                </div>
                <div onClick={() => handleEventSort('city')} className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] cursor-pointer hover:text-zinc-800 transition-colors w-[15%] flex items-center">
                  City {renderEventSortIcon('city')}
                </div>
                <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[20%] flex items-center">
                  Location / Venue
                </div>
                <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[25%] flex items-center">
                  Notes
                </div>
              </div>
              
              {/* Events Body */}
              <div className="flex-1 overflow-auto custom-scrollbar relative">
                {processedEvents.length === 0 ? (
                  <div className="py-16 text-center text-zinc-400 font-bold text-sm">No events match your filters.</div>
                ) : (
                  <div className="flex flex-col w-full">
                    {processedEvents.map((event, idx) => (
                      <div
                        key={event.id || idx}
                        onClick={() => {
                          dispatch({ type: 'OPEN_ADD_EVENT', payload: { ...event, isEdit: true, source: 'database' } });
                        }}
                        className={`border-b border-zinc-100 hover:bg-blue-50/50 transition-colors group flex flex-col cursor-pointer bg-white`}
                      >
                        <div className="flex min-h-[40px] items-stretch border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                          <div className={`px-3 py-1.5 align-top w-[25%] flex flex-col justify-start`}>
                            <span className={`font-black text-[11px] text-zinc-900 truncate ${event.type === 'tentative' ? 'italic' : ''}`}>
                              {event.title || 'Untitled Event'}
                            </span>
                            <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mt-0.5">
                              {event.type}
                            </span>
                          </div>
                          
                          <div className={`px-3 py-1.5 align-top w-[15%] flex flex-col justify-start`}>
                            <span className="text-[10px] font-bold text-zinc-600">
                              {event.startDate}
                            </span>
                            {event.endDate && event.endDate !== event.startDate && (
                              <span className="text-[9px] text-zinc-400">
                                to {event.endDate}
                              </span>
                            )}
                          </div>
                          
                          <div className={`px-3 py-1.5 align-top w-[15%] flex flex-col justify-start`}>
                            <span className="text-[11px] font-semibold text-zinc-700 truncate">
                              {event.city || '-'}
                            </span>
                          </div>
                          
                          <div className={`px-3 py-1.5 align-top w-[20%] flex flex-col justify-start`}>
                            <span className="text-[10px] font-bold text-zinc-700 truncate">
                              {event.venueName || event.address || '-'}
                            </span>
                          </div>
                          
                          <div className={`px-3 py-1.5 align-top w-[25%] flex flex-col justify-start`}>
                            <span className="text-[10px] font-medium italic text-zinc-600 line-clamp-2">
                              {event.note || '-'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : activeTab === 'tasks' ? (
            <>
              {/* Tasks Header Row */}
              <div className="flex bg-zinc-50 border-b border-zinc-200 select-none shrink-0">
                <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[50%] flex items-center">
                  Task Title
                </div>
                <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[20%] flex items-center">
                  Status
                </div>
                <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[30%] flex items-center">
                  Project / Channel
                </div>
              </div>
              
              {/* Tasks Body */}
              <div className="flex-1 overflow-auto custom-scrollbar relative">
                {allTasks.length === 0 ? (
                  <div className="py-16 text-center text-zinc-400 font-bold text-sm">No tasks found.</div>
                ) : (
                  <div className="flex flex-col w-full">
                    {allTasks.map((task, idx) => (
                      <div
                        key={task.id || idx}
                        className="flex border-b border-zinc-100/50 hover:bg-zinc-50/80 transition-colors w-full min-h-[44px] cursor-pointer"
                        onClick={() => {
                          if (task.channelId && task.channelId !== 'global') {
                            navigate(`/p/${task.channelId}/tasks`);
                          }
                        }}
                      >
                        <div className="px-3 py-3 w-[50%] text-sm text-zinc-900 font-medium truncate flex items-center gap-2">
                          <span className={task.isHeader ? 'font-bold' : ''}>{task.title}</span>
                          {task.isHeader && <span className="text-[10px] bg-zinc-100 px-1.5 py-0.5 rounded text-zinc-500 font-bold">FOLDER</span>}
                        </div>
                        <div className="px-3 py-3 w-[20%] text-xs text-zinc-500 flex items-center">
                          {task.completed ? 'Completed' : 'Active'}
                        </div>
                        <div className="px-3 py-3 w-[30%] text-xs text-zinc-500 truncate flex items-center">
                          {task.channelId === 'global' ? 'Personal' : (task.channelId || 'Unknown')}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : activeTab === 'habits' ? (
            <>
              {/* Habits Header Row */}
              <div className="flex bg-zinc-50 border-b border-zinc-200 select-none shrink-0">
                <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[40%] flex items-center">
                  Habit Name
                </div>
                <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[20%] flex items-center">
                  Frequency
                </div>
                <div className="px-3 py-1.5 font-black text-zinc-500 uppercase tracking-widest text-[9px] w-[40%] flex items-center">
                  Custom Parameters
                </div>
              </div>
              
              {/* Habits Body */}
              <div className="flex-1 overflow-auto custom-scrollbar relative">
                {habits.length === 0 ? (
                  <div className="py-16 text-center text-zinc-400 font-bold text-sm">No habits found.</div>
                ) : (
                  <div className="flex flex-col w-full">
                    {habits.map((habit, idx) => (
                      <div
                        key={habit.id || idx}
                        className="flex border-b border-zinc-100/50 hover:bg-zinc-50/80 transition-colors w-full min-h-[44px]"
                      >
                        <div className="px-3 py-3 w-[40%] text-sm text-zinc-900 font-medium truncate flex items-center gap-2">
                          {habit.icon && <span>{habit.icon}</span>}
                          {habit.name}
                        </div>
                        <div className="px-3 py-3 w-[20%] text-xs text-zinc-500 flex items-center">
                          {habit.frequency || 'Daily'}
                        </div>
                        <div className="px-3 py-3 w-[40%] text-xs text-zinc-500 flex flex-wrap gap-1 items-center">
                          {habit.parameters && Object.keys(habit.parameters).length > 0 ? (
                            Object.entries(habit.parameters).map(([key, p], i) => (
                              <span key={i} className="bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded text-[10px]">
                                {p.name} ({p.type})
                              </span>
                            ))
                          ) : (
                            <span className="text-zinc-400">None</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
        </div>
      </div>
    </div>
  );
}
